import { apiClient } from '@/core/api/client';
import { queryClient } from '@/core/config/queryClient';
import type { FpEngagementInput, GenerationMode } from './navigator-generation.types';

type CareContextPayload = { session_id: string; care_stage_id: string };

type JourneySummaryResponse = {
  session_id?: string;
  care_stage_id?: string;
  data?: Partial<CareContextPayload>;
  result?: Partial<CareContextPayload>;
  journey_summary?: Partial<CareContextPayload>;
};

const getCareContext = (responseData: JourneySummaryResponse): CareContextPayload | null => {
  const candidates = [
    responseData,
    responseData.data,
    responseData.result,
    responseData.journey_summary,
  ];
  for (const payload of candidates) {
    if (payload?.session_id && payload.care_stage_id) {
      return { session_id: payload.session_id, care_stage_id: payload.care_stage_id };
    }
  }
  return null;
};

/**
 * Journey-summary chain. Mirrors features/chatbot/components/fpQaCompletion.ts
 * (duplicated because shared code must not import from a feature). Writes
 * care_plans.conversation_summary — the input the matchmaking AI ranks against —
 * so the orchestrator awaits it BEFORE /care-options kicks the pipeline.
 */
export const runJourneySummaryChain = async (sessionId: string): Promise<void> => {
  const { data: journeyPayload } = await apiClient.get('/navigator/generate-journey-summary', {
    params: { session_id: sessionId },
  });
  const { data: responseData } = await apiClient.post<JourneySummaryResponse>(
    '/care-engine/journey-summary',
    journeyPayload,
  );
  const careContext = getCareContext(responseData);
  if (careContext) {
    await apiClient.post('/resources/store-care-context', careContext);
  }
};

// Query keys, staleTime and result shapes below are pinned to the consumers'
// own useQuery calls so each prefetch lands as a cache hit, not a duplicate:
// - care-seeker: care-navigator/components/CareSeekerResults.tsx
// - fp:          dashboard/financial-advisor/components/qa-tab/ResultsPage.tsx

/**
 * Hub resources are DB-driven, but keyed off `navigator_matchmaking_searches`
 * rows that only exist once matching is READY — callers must not fire this
 * before then (see the ordering note on `startGeneration` in
 * navigatorGenerationStore.ts).
 */
export const prefetchHubResources = (sessionId: string, mode: GenerationMode): Promise<void> =>
  queryClient.prefetchQuery({
    queryKey:
      mode === 'fp'
        ? ['fp-qa-hub-resources-session', sessionId]
        : ['care-seeker-hub-resources', sessionId],
    staleTime: mode === 'fp' ? 1000 * 60 * 5 : Infinity,
    queryFn: async () => {
      const { data } = await apiClient.get('/find-providers/hub-resources', {
        params: { session_id: sessionId },
        skipAuthRedirect: true,
      });
      if (mode === 'fp') {
        return {
          specialtyResourcesBySpecialtyId: data?.specialtyResourcesBySpecialtyId ?? {},
          careStageResources: data?.careStageResources ?? [],
        };
      }
      return data;
    },
  });

/** Suggested resources read the care context stored by the journey-summary chain. */
export const prefetchSuggestedResources = (sessionId: string): Promise<void> =>
  queryClient.prefetchQuery({
    queryKey: ['care-seeker-suggested-resources', sessionId],
    staleTime: Infinity,
    queryFn: async () => {
      const { data } = await apiClient.get<{ resources?: unknown[] }>(
        '/resources/suggested-resources',
        { params: { session_id: sessionId }, skipAuthRedirect: true },
      );
      return data.resources ?? [];
    },
  });

export const createFpEngagement = async (
  sessionId: string,
  input: FpEngagementInput,
): Promise<string> => {
  const { data } = await apiClient.post<{ id: string }>('/financial-advisors/me/qa-engagements', {
    navigatorSessionId: sessionId,
    careStageName: input.careStageName,
    ...(input.summaryBullets.length ? { summaryBullets: input.summaryBullets } : {}),
    ...(input.clientUserId ? { clientUserId: input.clientUserId } : {}),
    ...(input.name ? { name: input.name } : {}),
  });
  return data.id;
};
